import { ErrorAlert } from "@components/ErrorAlert";
import { queriesPayPeriods, queriesPayrollRuns, queriesWorkLogs } from "@query";
import {
  useWSModalCustom,
  useWSSnackbar,
  WSAlert,
  WSControl,
  WSDataItem,
  WSElement,
  WSInfoBox,
  WSList,
  WSLoader,
  WSModal,
  WSModalCustomProps,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  PayrollRun,
  PayrollRunCreate,
  PayrollRunType,
  WorkLog,
  WorkLogStatus
} from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo, useState } from "react";

import {
  bulkActionFactory,
  BulkActionProgress
} from "../../../modules/BulkActions";
import { PAYROLL_RUNS_OPEN_STATUSES } from "../constants";
import { formatPayPeriodRange } from "../utils";

/**
 * Resolves to the `PayrollRun` the caller should open — the freshly created one,
 * or the pay period's open run behind "View payroll" — or `undefined` when the
 * user just closed the modal.
 *
 * `useWSModalCustom` renders this from the app-level modal host, outside the
 * surface that opened it, so the payroll type arrives as a prop from the caller.
 */
type Props = {
  type: PayrollRunType;
} & WSModalCustomProps<PayrollRun | undefined>;

const ModalRunOffCyclePayroll: React.FC<Props> = props => {
  const { openSnackbar } = useWSSnackbar();

  const [payPeriodId, setPayPeriodId] = useState<string | null>(null);
  const [autoApprove, setAutoApprove] = useState(false);
  // Non-null only while the auto-approve batch is running (drives the progress view).
  const [approveProgress, setApproveProgress] =
    useState<BulkActionProgress | null>(null);

  const [createPayroll, createMeta] =
    queriesPayrollRuns.createOffCyclePayrollRun.useMutation();
  // Approving one work log at a time (no bulk endpoint). Suppress the per-call
  // sibling-read invalidation — we approve up to N in a tight loop, then the
  // caller navigates to the new run (which refetches), so one storm of
  // invalidations mid-loop would be wasted work.
  const [approveWorkLog] = queriesWorkLogs.approveWorkLog.useMutation({
    defaultDependenciesLevel: "none"
  });

  const queryPayPeriods = queriesPayPeriods.listPayPeriods.useQueryAll();
  // In-review = submitted-but-unpaid, scoped to the selected period server-side
  // via `filter[payPeriodId][eq]`. Disabled until a period is picked.
  const queryWorkLogs = queriesWorkLogs.listWorkLogs.useQueryAll(
    {
      filter: {
        status: { anyOf: [WorkLogStatus.IN_REVIEW] },
        payPeriodId: { eq: payPeriodId as string }
      }
    },
    { enabled: !!payPeriodId }
  );
  // Only an *open* run blocks an off-cycle run for the same period (see
  // `PAYROLL_RUNS_OPEN_STATUSES`) — a finalized/paid/cancelled run is precisely
  // the case off-cycle payroll exists for, so it must not block. Statuses are
  // filtered server-side; `listPayrollRuns` has no pay-period filter, so the
  // period match itself stays client-side over that (small) open set.
  const queryPayrolls = queriesPayrollRuns.listPayrollRuns.useQueryAll({
    filter: {
      type: { eq: props.type },
      status: { anyOf: PAYROLL_RUNS_OPEN_STATUSES }
    }
  });

  const payPeriodOptions = useMemo(
    () =>
      (queryPayPeriods.flatData ?? [])
        .filter(payPeriod => !!payPeriod.id)
        .map(payPeriod => ({
          value: payPeriod.id as string,
          label: payPeriod.payPeriodNumber
            ? `Pay period ${payPeriod.payPeriodNumber} · ${formatPayPeriodRange(
                payPeriod.startDate,
                payPeriod.endDate
              )}`
            : formatPayPeriodRange(payPeriod.startDate, payPeriod.endDate)
        })),
    [queryPayPeriods.flatData]
  );

  const unpaidWorkLogs = useMemo(
    () => (payPeriodId ? (queryWorkLogs.flatData ?? []) : []),
    [queryWorkLogs.flatData, payPeriodId]
  );

  // Distinct contractors covered by the unpaid work logs. The v3 `WorkLog` has no
  // payeeId, so `payeeEngagementId` (the contractor↔payer relationship) is the
  // grouping key, falling back to `engagementId`.
  const employeeCount = useMemo(
    () =>
      new Set(
        unpaidWorkLogs
          .map(workLog => workLog.payeeEngagementId ?? workLog.engagementId)
          .filter(Boolean)
      ).size,
    [unpaidWorkLogs]
  );

  const openRun = useMemo(
    () =>
      payPeriodId
        ? (queryPayrolls.flatData ?? []).find(
            payroll => payroll.payPeriodId === payPeriodId
          )
        : undefined,
    [queryPayrolls.flatData, payPeriodId]
  );

  const isLoadingPeriods = queryPayPeriods.isInitialLoading;
  const hasNoPayPeriods = !isLoadingPeriods && payPeriodOptions.length === 0;
  const isCheckingScope =
    queryWorkLogs.isInitialLoading || queryPayrolls.isInitialLoading;
  const willAutoApprove = autoApprove && unpaidWorkLogs.length > 0;
  const isProcessing = approveProgress !== null || createMeta.isLoading;

  const handleSubmit = async () => {
    if (!payPeriodId || openRun) {
      return;
    }
    try {
      if (willAutoApprove) {
        setApproveProgress({
          total: unpaidWorkLogs.length,
          processed: 0,
          successful: 0,
          failed: 0
        });
        const bulk = bulkActionFactory<WorkLog>({
          onProgress: setApproveProgress,
          action: async workLog => {
            if (workLog.id) {
              await approveWorkLog({ workLogId: workLog.id });
            }
          }
        });
        await bulk.run(unpaidWorkLogs);
        setApproveProgress(null);
      }

      const payroll = await createPayroll({
        // TODO(v3-blocker): `PayrollRunCreate` is uninhabitable — the generated
        // union intersects the discriminator literal (`type: 'Employee'`) with
        // the nominal enum typing `EmployeeOffCyclePayrollRunCreate.type`, and
        // that intersection reduces to `never`, so NO value type-checks against
        // it (re-verified on SDK 3.29.0 for `PayrollRunType.EMPLOYEE`,
        // `EmployeeOffCyclePayrollRunCreateType.EMPLOYEE` and the bare literal —
        // still reproducing, first seen on 3.15.1). The runtime is unaffected:
        // the serializer switches on the `"Employee"` string and writes `type` +
        // `payPeriodId` to the wire unchanged. Drop the cast once the SDK emits
        // the discriminator as a plain literal.
        payrollRunCreate: {
          type: props.type,
          payPeriodId
        } as unknown as PayrollRunCreate
      });
      openSnackbar({
        icon: { name: "check" },
        message: "Off-cycle payroll created"
      });
      props.onClose(payroll);
    } catch {
      // The create error surfaces via `createMeta.error` (ErrorAlert below);
      // drop the progress view so the user can retry from the form.
      setApproveProgress(null);
    }
  };

  const renderBody = () => {
    if (isProcessing) {
      return (
        <WSList gap="M">
          <WSLoader.Spinner />
          <WSText>
            {approveProgress
              ? `Approving unpaid work logs… ${approveProgress.processed} of ${approveProgress.total}`
              : "Creating payroll run…"}
          </WSText>
        </WSList>
      );
    }

    if (isLoadingPeriods) {
      return <WSLoader.Spinner />;
    }

    if (hasNoPayPeriods) {
      return (
        <WSInfoBox>
          <WSText.ParagraphSm color="gray500">
            No pay period found. There are no pay periods available to run an
            off-cycle payroll for.
          </WSText.ParagraphSm>
        </WSInfoBox>
      );
    }

    return (
      <WSList gap="M">
        <WSText color="gray500">
          Run a payroll outside the regular schedule. Select the pay period to
          pay work logs for.
        </WSText>

        <WSElement>
          <WSText.ParagraphSm color="gray500" mb="XS">
            Pay period
          </WSText.ParagraphSm>
          <WSSelect
            mode="single"
            options={payPeriodOptions}
            placeholder="Select a pay period"
            value={payPeriodId}
            onChange={value => setPayPeriodId(value ?? null)}
          />
        </WSElement>

        {payPeriodId &&
          !isCheckingScope &&
          (openRun ? (
            <WSAlert
              action={{
                text: "View payroll",
                // Resolving with the run hands it to the caller, which opens its
                // details page — the modal stays out of routing.
                onClick: () => props.onClose(openRun)
              }}
              icon="alert"
              theme="warning"
              title="Open payroll run found for this pay period"
            >
              Every work log approved for this pay period will be included in
              the existing payroll run. Finalize the existing payroll run to
              create an off-cycle payroll run.
            </WSAlert>
          ) : unpaidWorkLogs.length > 0 ? (
            <>
              <WSInfoBox>
                <WSList gap="S">
                  <WSDataItem
                    label="Number of work logs"
                    value={String(unpaidWorkLogs.length)}
                  />
                  <WSDataItem
                    label="Number of employees within scope"
                    value={String(employeeCount)}
                  />
                </WSList>
              </WSInfoBox>
              <WSControl
                label="Auto-approve unpaid work logs"
                size="S"
                type="checkbox"
                value={autoApprove}
                onChange={setAutoApprove}
              />
            </>
          ) : (
            <WSInfoBox>
              <WSText.ParagraphSm color="gray500">
                No unpaid work logs found for this pay period.
              </WSText.ParagraphSm>
            </WSInfoBox>
          ))}

        <ErrorAlert context="OffCyclePayroll" error={createMeta.error} />
      </WSList>
    );
  };

  return (
    <WSModal
      actions={{
        buttons: [
          {
            label: "Cancel",
            kind: "Secondary",
            type: "button",
            disabled: isProcessing,
            onClick: () => props.onClose(undefined)
          },
          {
            label: willAutoApprove ? "Approve & run payroll" : "Run payroll",
            loading: isProcessing,
            disabled: !payPeriodId || !!openRun || hasNoPayPeriods,
            onClick: handleSubmit
          }
        ]
      }}
      size="S"
      title="Run off-cycle payroll"
      onClose={() => props.onClose(undefined)}
    >
      {renderBody()}
    </WSModal>
  );
};

export const useModalRunOffCyclePayroll = () =>
  useWSModalCustom(ModalRunOffCyclePayroll);
